import Alpine, { AlpineComponent } from 'alpinejs';
import { SavRules } from '../../types/form';
import { iodine } from '../../helpers/validation';
import { FormFieldValidationAlpine, getFieldValidation } from '../../helpers/fieldValidation';

interface ContactFormAlpine extends AlpineComponent<Record<string, any>> {
    [key: string]: any;
    form: HTMLFormElement;
    validate: () => void;
    formatFieldOrderNumber: (orderInput: HTMLTextAreaElement, event: InputEvent) => void;
}

const rules: SavRules = {
    civility: ['truthy'],
    firstname: ['required'],
    lastname: ['required'],
    address: ['required'],
    zip: ['required'],
    city: ['required'],
    phone: ['required', 'trybaPhone'],
    email: ['required', 'email', 'noPlusInEmail'],
    need: ['truthy'],
    yearPose: ['required'],
    concessionnaire: ['required'],
    orderNumber: [],
    message: ['required'],
    media: ['required'],
    fonction: [],
    fonctionAutre: [],
};

document.addEventListener('alpine:init', () => {
    // @ts-ignore
    Alpine.data('initFormValidation', (form: HTMLFormElement): ContactFormAlpine & FormFieldValidationAlpine => {
        // Le formulaire presse n'a ni localisation (address/zip/city) ni téléphone/message obligatoires :
        // ces refs n'existent pas dans le DOM et l'accès à `.value` lèverait une exception.
        const isPresseForm = form.name === 'presse_form';

        // `rules` est partagé par les 3 formulaires (contact/SAV/presse) et capturé par référence dans
        // getFieldValidation() : validateField() lit `rules[name]` à chaque appel, pas seulement à l'initialisation.
        // Une page ne charge qu'un seul de ces formulaires, donc neutraliser phone/message ici, avant de le
        // transmettre, ne peut pas affecter le comportement de contact ou SAV sur une autre page.
        if (isPresseForm) {
            // 'optional' fait sauter les règles suivantes tant que le champ est vide (cf. Iodine#_prepare) ;
            // rempli, le format trybaPhone reste vérifié — même comportement que le ternaire de validate() ci-dessous.
            rules.phone = ['optional', 'trybaPhone'];
            // Le message n'a pas de contrainte de format sur ce formulaire : un tableau vide le rend
            // valide qu'il soit rempli ou non.
            rules.message = [];
        }

        return {
            ...getFieldValidation(rules),
            ...{
                form,
                validate(): void {
                    this.errorMessages = [];

                    const firstname = this.$refs.firstname as HTMLInputElement;
                    const lastname = this.$refs.lastname as HTMLInputElement;
                    const phone = this.$refs.phone as HTMLInputElement;
                    const email = this.$refs.email as HTMLInputElement;
                    const civilityMadam = this.$refs.civility_madam as HTMLInputElement;
                    const civilitySir = this.$refs.civility_sir as HTMLInputElement;

                    let contactItems: Record<string, any> = {
                        firstname: firstname.value,
                        lastname: lastname.value,
                        phone: phone.value,
                        email: email.value,
                        civility: civilityMadam.checked || civilitySir.checked,
                    };

                    let contactRules: Record<string, any> = {
                        firstname: rules.firstname,
                        lastname: rules.lastname,
                        // Le téléphone est facultatif côté presse : on ne vérifie le format que s'il est renseigné.
                        phone: isPresseForm && !phone.value ? [] : rules.phone,
                        email: rules.email,
                        civility: rules.civility,
                    };

                    if (!isPresseForm) {
                        const address = this.$refs.address as HTMLInputElement;
                        // as zip is in x-data=simpleAutocomplete, it is not accessible in refs any more
                        const zip = document.querySelector('[name$="[zip]"]') as HTMLInputElement;
                        const city = this.$refs.city as HTMLInputElement;
                        const message = this.$refs.message as HTMLTextAreaElement;

                        contactItems = {
                            ...contactItems,
                            address: address.value,
                            zip: zip.value,
                            city: city.value,
                            message: message.value,
                        };

                        contactRules = {
                            ...contactRules,
                            address: rules.address,
                            zip: rules.zip,
                            city: rules.city,
                            message: rules.message,
                        };
                    } else {
                        const media = this.$refs.media as HTMLInputElement;

                        contactItems = {
                            ...contactItems,
                            media: media.value,
                        };

                        contactRules = {
                            ...contactRules,
                            media: rules.media,
                        };
                    }

                    let savItems = {};
                    let savRules = {};

                    if (form.name === 'sav_form') {
                        /**
                         * On enlève les champs de fichier, car ils sont passés via l'encodage base64 sur le formulaire SAV
                         * avec les name "sav_form_invoices_b64" et "sav_form_photos_b64". Ces données sont ensuite traitées côté serveur.
                         * Cependant la limite du serveur est de 16Mo, donc pour ne pas atteindre la limite on supprime les fichiers
                         * qui seraient aussi envoyés normalement dans le formulaire via "sav_form[invoices][]" et "sav_form[photos][]" et qui
                         * donc contriburait a atteindre la taille limite de 16Mo.
                         */
                        Array.from(form.querySelectorAll('[name*="sav_form[invoices][]"], [name*="sav_form[photos][]"]')).forEach(fileInput => fileInput.remove());
                        const needTechnicien = this.$refs.need_technicien as HTMLInputElement;
                        const needFourniture = this.$refs.need_fourniture as HTMLInputElement;
                        const yearPose = this.$refs.yearPose as HTMLInputElement;
                        const concessionnaire = this.$refs.concessionnaire as HTMLInputElement;
                        const orderNumber = this.$refs.concessionnaire as HTMLInputElement;
                        savItems = {
                            need: needTechnicien.checked || needFourniture.checked,
                            yearPose: yearPose.value,
                            concessionnaire: concessionnaire.value,
                            orderNumber: orderNumber.value,
                        };
                        savRules = {
                            need: rules.need,
                            yearPose: rules.yearPose,
                            concessionnaire: rules.concessionnaire,
                            orderNumber: rules.orderNumber,
                        };
                    }

                    const validation = iodine.assert({ ...contactItems, ...savItems }, { ...contactRules, ...savRules });

                    const submitButton = form.querySelector('button[type=submit]') as HTMLButtonElement;
                    if (validation.valid) {
                        submitButton.disabled = true;
                        form.submit();
                    } else {
                        submitButton.disabled = false;
                        this.displayErrors(validation);
                    }
                },
                formatFieldOrderNumber(orderInput: HTMLTextAreaElement, event: InputEvent): void {
                    if (event.data && new RegExp(/^[a-zA-Z]+$/, 'g').test(event.data)) {
                        orderInput.value = '92';
                    }
                },
            },
        };
    });
});
